import React from "react";
import { motion } from "framer-motion";

import {
  FaArrowRight,
  FaMusic,
  FaMicrophone,
  FaGuitar,
  FaTheaterMasks,
  FaStar,
  FaHeart,
  FaUsers,
  FaAward,
} from "react-icons/fa";

import musicImg from "../assets/music1.jpeg";
import musicImg2 from "../assets/music2.jpeg";

const MusicDance = () => {
  return (
    <section className="bg-white overflow-hidden">

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <section className="relative bg-[#f8fafc] overflow-hidden">

        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full border-[70px] border-[#d4af37]/10" />

        <div className="absolute bottom-[-180px] -left-20 w-[420px] h-[420px] rounded-full border-[60px] border-[#07294d]/5" />

        <div className="absolute top-28 left-[8%] w-4 h-4 rounded-full bg-[#d4af37]" />

        <div className="absolute top-48 right-[12%] w-3 h-3 rounded-full bg-[#07294d]/20" />

        <div className="max-w-[1450px] mx-auto px-6 py-20 md:py-28 relative z-10">

          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center"
          >

            <p className="uppercase tracking-[6px] text-[#d4af37] font-bold mb-5">
              Cambridge International School
            </p>

            <h1 className="text-5xl md:text-7xl xl:text-8xl font-black text-[#07294d] leading-[0.95]">
              Music & Dance
            </h1>

            <div className="w-28 h-[5px] bg-[#d4af37] rounded-full mx-auto mt-7 mb-8" />

            <p className="max-w-3xl mx-auto text-lg text-gray-600 leading-9">
              Encouraging creativity, confidence and self-expression
              through music, dance and performing arts.
            </p>

            <div className="flex justify-center items-center gap-4 mt-10">

              <div className="w-12 h-12 rounded-2xl bg-[#07294d] text-white flex items-center justify-center">
                <FaMusic />
              </div>

              <div className="w-3 h-3 rounded-full bg-[#d4af37]" />

              <div className="w-12 h-12 rounded-2xl bg-[#d4af37] text-[#07294d] flex items-center justify-center">
                <FaHeart />
              </div>

              <div className="w-3 h-3 rounded-full bg-[#d4af37]" />

              <div className="w-12 h-12 rounded-2xl bg-[#07294d] text-white flex items-center justify-center">
                <FaUsers />
              </div>

            </div>

          </motion.div>

        </div>

      </section>


      {/* =====================================================
          ABOUT
      ===================================================== */}

      <section className="relative py-24 md:py-28 bg-white">

        <div className="absolute top-20 right-0 w-72 h-72 bg-[#d4af37]/5 rounded-full blur-3xl" />

        <div className="absolute bottom-10 left-0 w-72 h-72 bg-[#07294d]/5 rounded-full blur-3xl" />

        <div className="max-w-[1450px] mx-auto px-6 relative z-10">

          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* =================================================
                CONTENT
            ================================================= */}

            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-7"
            >

              <span className="uppercase tracking-[5px] text-[#d4af37] font-bold">
                Creative Arts
              </span>

              <h2 className="text-5xl md:text-6xl font-black text-[#07294d] leading-tight mt-5 mb-8">
                Inspiring Creativity
                <br />
                Through Performing Arts
              </h2>

              <div className="w-24 h-[5px] bg-[#d4af37] rounded-full mb-10" />

              <p className="text-gray-600 text-lg leading-9 mb-6">
                Music and dance are an integral part of holistic
                education at Cambridge International School.
                Students are encouraged to discover their talents,
                express themselves confidently and develop artistic
                excellence.
              </p>

              <p className="text-gray-600 text-lg leading-9 mb-6">
                Through structured training, performances and
                cultural activities, students strengthen creativity,
                discipline, teamwork and communication skills.
              </p>

              <p className="text-gray-600 text-lg leading-9 mb-10">
                Our experienced mentors provide opportunities for
                every child to participate in vocal music,
                instrumental learning, dance performances and
                stage presentations.
              </p>

              {/* Mini Stats */}

              <div className="grid grid-cols-3 gap-4 mt-12 max-w-xl">

                <div className="border-l-4 border-[#d4af37] pl-4">

                  <FaMusic className="text-[#d4af37] text-xl mb-2" />

                  <p className="font-black text-[#07294d]">
                    Music
                  </p>

                  <p className="text-sm text-gray-500">
                    Learning
                  </p>

                </div>


                <div className="border-l-4 border-[#d4af37] pl-4">

                  <FaHeart className="text-[#d4af37] text-xl mb-2" />

                  <p className="font-black text-[#07294d]">
                    Creativity
                  </p>

                  <p className="text-sm text-gray-500">
                    Expression
                  </p>

                </div>


                <div className="border-l-4 border-[#d4af37] pl-4">

                  <FaUsers className="text-[#d4af37] text-xl mb-2" />

                  <p className="font-black text-[#07294d]">
                    Teamwork
                  </p>

                  <p className="text-sm text-gray-500">
                    Confidence
                  </p>

                </div>

              </div>

            </motion.div>


            {/* =================================================
                TWO SEPARATE IMAGES
            ================================================= */}

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-5 relative"
            >

              {/* Decorative circle */}

              <div className="absolute -top-8 -right-8 w-28 h-28 border-[18px] border-[#d4af37]/20 rounded-full" />

              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-[#07294d]/5 rounded-full" />


              {/* IMAGE GRID */}

              <div className="relative z-10 grid grid-cols-2 gap-5 items-start">

                {/* =========================================
                    IMAGE 1
                ========================================= */}

                <motion.div
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.7,
                    delay: 0.1,
                  }}
                  viewport={{ once: true }}
                  className="relative"
                >

                  {/* Gold decorative shadow */}

                  <div className="absolute inset-0 bg-[#d4af37]/20 rounded-[32px] translate-x-3 translate-y-3" />

                  <div className="relative bg-white p-2 rounded-[32px] shadow-2xl">

                    <div className="overflow-hidden rounded-[25px]">

                      <img
                        src={musicImg}
                        alt="Music and Dance"
                        className="w-full h-auto block hover:scale-105 transition duration-700"
                      />

                    </div>

                  </div>

                </motion.div>


                {/* =========================================
                    IMAGE 2
                ========================================= */}

                <motion.div
                  initial={{ opacity: 0, y: 35 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.7,
                    delay: 0.25,
                  }}
                  viewport={{ once: true }}
                  className="relative mt-14"
                >

                  {/* Navy decorative shadow */}

                  <div className="absolute inset-0 bg-[#07294d]/10 rounded-[32px] translate-x-3 translate-y-3" />

                  <div className="relative bg-white p-2 rounded-[32px] shadow-2xl">

                    <div className="overflow-hidden rounded-[25px]">

                      <img
                        src={musicImg2}
                        alt="Music and Dance Activities"
                        className="w-full h-auto block hover:scale-105 transition duration-700"
                      />

                    </div>

                  </div>

                </motion.div>

              </div>


              {/* =========================================
                  FLOATING CARD
              ========================================= */}

              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                }}
                className="
                  relative
                  z-30
                  -mt-10
                  ml-[-20px]
                  w-fit
                  bg-white
                  rounded-[25px]
                  shadow-2xl
                  p-5
                  flex
                  items-center
                  gap-4
                "
              >

                <div className="w-14 h-14 rounded-2xl bg-[#07294d] text-[#d4af37] flex items-center justify-center text-2xl shrink-0">
                  <FaStar />
                </div>

                <div>

                  <p className="text-xs uppercase tracking-[2px] text-[#d4af37] font-bold">
                    Performing Arts
                  </p>

                  <p className="font-black text-[#07294d] text-lg">
                    Discover Your Talent
                  </p>

                </div>

              </motion.div>

            </motion.div>

          </div>

        </div>

      </section>


      {/* =====================================================
          PROGRAMS
      ===================================================== */}

      <section className="relative py-24 md:py-28 bg-[#f8fafc] overflow-hidden">

        <div className="absolute top-0 left-0 w-64 h-64 bg-[#d4af37]/5 rounded-full blur-3xl" />

        <div className="absolute bottom-0 right-0 w-72 h-72 bg-[#07294d]/5 rounded-full blur-3xl" />

        <div className="max-w-[1450px] mx-auto px-6 relative z-10">

          <div className="text-center mb-20">

            <p className="uppercase tracking-[6px] text-[#d4af37] font-bold mb-4">
              Programs & Activities
            </p>

            <h2 className="text-5xl md:text-6xl font-black text-[#07294d]">
              Discover Your Talent
            </h2>

            <div className="w-24 h-[5px] bg-[#d4af37] rounded-full mx-auto mt-6" />

          </div>


          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-7">

            {[
              {
                icon: "🎤",
                title: "Music Training",
                desc: "Professional guidance in vocal music and instrumental learning.",
              },
              {
                icon: "💃",
                title: "Dance Classes",
                desc: "Students learn various dance forms while building confidence and expression.",
              },
              {
                icon: "🎸",
                title: "Instrument Practice",
                desc: "Hands-on practice sessions with musical instruments to develop rhythm and creativity.",
              },
              {
                icon: "🎭",
                title: "Stage Performances",
                desc: "Students participate in assemblies, annual functions and cultural events.",
              },
            ].map((item, index) => (

              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 35,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.08,
                }}
                viewport={{
                  once: true,
                }}
                whileHover={{
                  y: -10,
                }}
                className="relative bg-white rounded-[30px] p-8 shadow-lg border border-gray-100 transition duration-300 overflow-hidden group"
              >

                <div className="absolute right-4 top-1 text-7xl font-black text-gray-50 group-hover:text-[#d4af37]/10 transition">
                  0{index + 1}
                </div>


                <div className="relative z-10 w-16 h-16 rounded-2xl bg-[#07294d] text-white flex items-center justify-center text-3xl mb-6 group-hover:bg-[#d4af37] group-hover:text-[#07294d] transition">
                  {item.icon}
                </div>


                <h3 className="relative z-10 text-2xl font-bold text-[#07294d] mb-4">
                  {item.title}
                </h3>


                <div className="w-14 h-[4px] bg-[#d4af37] rounded-full mb-5" />


                <p className="relative z-10 text-gray-600 leading-8">
                  {item.desc}
                </p>

              </motion.div>

            ))}

          </div>

        </div>

      </section>


      {/* =====================================================
          CREATIVE BREAK
      ===================================================== */}

      <section className="py-16 bg-white">

        <div className="max-w-[1250px] mx-auto px-6">

          <div className="bg-[#07294d] rounded-[40px] px-8 py-10 md:px-14 flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden">

            <div className="absolute -right-16 -top-16 w-52 h-52 border-[35px] border-[#d4af37]/10 rounded-full" />

            <div className="absolute -left-10 -bottom-20 w-48 h-48 border-[25px] border-white/5 rounded-full" />


            <div className="relative z-10 flex items-center gap-6">

              <div className="w-16 h-16 rounded-2xl bg-[#d4af37] text-[#07294d] flex items-center justify-center text-2xl">
                <FaMusic />
              </div>

              <div>

                <p className="text-[#d4af37] uppercase tracking-[3px] text-xs font-bold">
                  Performing Arts
                </p>

                <h3 className="text-2xl md:text-3xl font-black text-white">
                  Creativity Has No Limits
                </h3>

              </div>

            </div>


            <div className="relative z-10 flex gap-3">

              <div className="px-5 py-3 rounded-full bg-white/10 text-white text-sm font-semibold">
                Music
              </div>

              <div className="px-5 py-3 rounded-full bg-white/10 text-white text-sm font-semibold">
                Dance
              </div>

              <div className="px-5 py-3 rounded-full bg-white/10 text-white text-sm font-semibold">
                Theatre
              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          WHY MUSIC & DANCE
      ===================================================== */}

      <section className="py-24 md:py-28 bg-white">

        <div className="max-w-[1450px] mx-auto px-6">

          <div className="grid lg:grid-cols-2 gap-20 items-center">

            {/* VISUAL SIDE */}

            <motion.div
              initial={{
                opacity: 0,
                x: -40,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              transition={{
                duration: 0.8,
              }}
              viewport={{
                once: true,
              }}
              className="relative"
            >

              <div className="relative bg-[#f8fafc] rounded-[45px] p-10 md:p-14 border border-gray-100 overflow-hidden">

                <div className="absolute -right-10 -top-16 text-[260px] text-[#d4af37]/10 font-black">
                  ♪
                </div>


                <div className="relative z-10">

                  <div className="flex items-center gap-5 mb-10">

                    <div className="w-20 h-20 rounded-[25px] bg-[#07294d] text-[#d4af37] flex items-center justify-center text-3xl shadow-xl">
                      <FaMusic />
                    </div>

                    <div>

                      <p className="uppercase tracking-[3px] text-[#d4af37] text-xs font-bold">
                        Creative Development
                      </p>

                      <h3 className="text-3xl font-black text-[#07294d]">
                        Learn. Express. Perform.
                      </h3>

                    </div>

                  </div>


                  <div className="grid grid-cols-2 gap-5">

                    <div className="bg-white rounded-[25px] p-6 shadow-md">

                      <FaMicrophone className="text-3xl text-[#d4af37] mb-5" />

                      <h4 className="font-black text-[#07294d] text-xl mb-2">
                        Voice
                      </h4>

                      <p className="text-gray-500 text-sm leading-6">
                        Discover expression through music.
                      </p>

                    </div>


                    <div className="bg-[#07294d] rounded-[25px] p-6 shadow-md">

                      <FaGuitar className="text-3xl text-[#d4af37] mb-5" />

                      <h4 className="font-black text-white text-xl mb-2">
                        Rhythm
                      </h4>

                      <p className="text-white/60 text-sm leading-6">
                        Build musical understanding.
                      </p>

                    </div>


                    <div className="bg-[#d4af37] rounded-[25px] p-6 shadow-md">

                      <FaTheaterMasks className="text-3xl text-[#07294d] mb-5" />

                      <h4 className="font-black text-[#07294d] text-xl mb-2">
                        Stage
                      </h4>

                      <p className="text-[#07294d]/70 text-sm leading-6">
                        Grow through performance.
                      </p>

                    </div>


                    <div className="bg-white rounded-[25px] p-6 shadow-md">

                      <FaAward className="text-3xl text-[#d4af37] mb-5" />

                      <h4 className="font-black text-[#07294d] text-xl mb-2">
                        Excellence
                      </h4>

                      <p className="text-gray-500 text-sm leading-6">
                        Celebrate every achievement.
                      </p>

                    </div>

                  </div>


                  <div className="flex gap-2 mt-8">

                    <FaStar className="text-[#d4af37]" />
                    <FaStar className="text-[#d4af37]" />
                    <FaStar className="text-[#d4af37]" />
                    <FaStar className="text-[#d4af37]" />
                    <FaStar className="text-[#d4af37]" />

                  </div>

                </div>

              </div>

            </motion.div>


            {/* CONTENT */}

            <motion.div
              initial={{
                opacity: 0,
                x: 40,
              }}
              whileInView={{
                opacity: 1,
                x: 0,
              }}
              transition={{
                duration: 0.8,
              }}
              viewport={{
                once: true,
              }}
            >

              <span className="uppercase tracking-[5px] text-[#d4af37] font-bold">
                Why Music & Dance?
              </span>


              <h2 className="text-5xl md:text-6xl font-black text-[#07294d] mt-5 mb-8">
                Building Confidence
                <br />
                Through Creativity
              </h2>


              <div className="w-24 h-[5px] bg-[#d4af37] rounded-full mb-10" />


              <p className="text-gray-600 text-lg leading-9 mb-8">
                Music and dance enhance imagination, confidence,
                discipline and teamwork. Students develop creativity
                while learning to express themselves through artistic
                performances.
              </p>


              <div className="space-y-5">

                {[
                  "Vocal & Instrumental Music",
                  "Dance Performances",
                  "Annual Cultural Events",
                  "Stage Confidence & Teamwork",
                ].map((item, index) => (

                  <div
                    key={index}
                    className="flex items-center gap-4"
                  >

                    <div className="w-10 h-10 rounded-full bg-[#d4af37] text-white flex items-center justify-center font-bold">
                      ✓
                    </div>

                    <p className="text-[#07294d] text-lg font-medium">
                      {item}
                    </p>

                  </div>

                ))}

              </div>

            </motion.div>

          </div>

        </div>

      </section>


      {/* =====================================================
          ACHIEVEMENTS
      ===================================================== */}

      <section className="py-24 bg-[#f8fafc]">

        <div className="max-w-[1450px] mx-auto px-6">

          <div className="text-center mb-20">

            <p className="uppercase tracking-[6px] text-[#d4af37] font-bold mb-4">
              Student Achievements
            </p>

            <h2 className="text-5xl md:text-6xl font-black text-[#07294d]">
              Celebrating Every Performance
            </h2>

            <div className="w-24 h-[5px] bg-[#d4af37] rounded-full mx-auto mt-6" />

            <p className="max-w-3xl mx-auto text-gray-600 text-lg leading-9 mt-8">
              Students regularly participate in cultural festivals,
              annual functions, inter-school competitions and
              stage performances, showcasing their artistic
              talents with confidence and enthusiasm.
            </p>

          </div>


          <div className="grid md:grid-cols-3 gap-8">

            <motion.div
              whileHover={{ y: -8 }}
              className="bg-white rounded-[30px] p-10 shadow-lg border border-gray-100 text-center"
            >

              <div className="text-5xl mb-5">
                🎵
              </div>

              <h3 className="text-2xl font-bold text-[#07294d] mb-4">
                Vocal Music
              </h3>

              <div className="w-12 h-[4px] bg-[#d4af37] rounded-full mx-auto mb-5" />

              <p className="text-gray-600 leading-8">
                Students receive structured vocal training
                and perform during school celebrations
                and competitions.
              </p>

            </motion.div>


            <motion.div
              whileHover={{ y: -8 }}
              className="bg-white rounded-[30px] p-10 shadow-lg border border-gray-100 text-center"
            >

              <div className="text-5xl mb-5">
                💃
              </div>

              <h3 className="text-2xl font-bold text-[#07294d] mb-4">
                Dance Performances
              </h3>

              <div className="w-12 h-[4px] bg-[#d4af37] rounded-full mx-auto mb-5" />

              <p className="text-gray-600 leading-8">
                Dance activities encourage creativity,
                confidence and teamwork while celebrating
                different cultures.
              </p>

            </motion.div>


            <motion.div
              whileHover={{ y: -8 }}
              className="bg-white rounded-[30px] p-10 shadow-lg border border-gray-100 text-center"
            >

              <div className="text-5xl mb-5">
                🎭
              </div>

              <h3 className="text-2xl font-bold text-[#07294d] mb-4">
                Cultural Events
              </h3>

              <div className="w-12 h-[4px] bg-[#d4af37] rounded-full mx-auto mb-5" />

              <p className="text-gray-600 leading-8">
                Annual functions and cultural programmes
                provide every student with an opportunity
                to shine on stage.
              </p>

            </motion.div>

          </div>

        </div>

      </section>


      {/* =====================================================
          CTA
      ===================================================== */}

      <section className="relative py-24 bg-[#07294d] overflow-hidden">

        <div className="absolute -top-32 -right-32 w-[450px] h-[450px] rounded-full border-[70px] border-[#d4af37]/10" />

        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full border-[60px] border-white/5" />

        <div className="max-w-[1100px] mx-auto px-6 relative z-10">

          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.8,
            }}
            viewport={{
              once: true,
            }}
            className="text-center"
          >

            <p className="uppercase tracking-[6px] text-[#d4af37] font-bold mb-5">
              Let Your Talent Shine
            </p>

            <h2 className="text-5xl md:text-6xl font-black text-white leading-tight">
              Discover the Joy of
              <br />
              Music & Dance
            </h2>

            <div className="w-24 h-[5px] bg-[#d4af37] rounded-full mx-auto mt-7 mb-8" />

            <p className="max-w-2xl mx-auto text-white/70 text-lg leading-9 mb-10">
              At Cambridge International School, every student
              gets an opportunity to explore creativity, build
              confidence and express themselves through the
              performing arts.
            </p>


            <div className="flex flex-col sm:flex-row justify-center gap-4">

              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-3 bg-[#d4af37] text-[#07294d] px-8 py-4 rounded-full font-bold hover:bg-white transition duration-300"
              >
                Contact Us
                <FaArrowRight />
              </a>

              <a
                href="/"
                className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-[#07294d] transition duration-300"
              >
                Explore School
                <FaArrowRight />
              </a>

            </div>

          </motion.div>

        </div>

      </section>

    </section>
  );
};

export default MusicDance;